import { useState, useEffect } from "react";
import { CircleNotch, ArrowClockwise, Lightbulb, ArrowRight, ListChecks, Printer, X, Sparkle, PaperPlaneRight, ArrowsOut, ArrowsIn, Megaphone, ArrowUUpLeft } from "@phosphor-icons/react";
import { ThinkingTimer } from "@/components/ThinkingTimer";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SystemsMap from "@/components/SystemsMap";
import { useFeedback } from "@/context/FeedbackContext";
import api from "@/lib/api";
import MicButton from "@/components/MicButton";

const LABEL = {
  systemic: { text: "Systemic", color: "#059669", bg: "rgba(5,150,105,0.1)" },
  partial: { text: "Partial", color: "#b45309", bg: "rgba(180,83,9,0.1)" },
  exploratory: { text: "Exploratory", color: "#0891b2", bg: "rgba(8,145,178,0.1)" },
  isolated: { text: "Isolated", color: "#be123c", bg: "rgba(190,18,60,0.1)" },
};
const labelOf = (l) => LABEL[l] || LABEL.exploratory;

const REVISE_SUGGESTIONS = [
  "This isn't actually systemic — the parts don't reinforce one another.",
  "You're solving symptoms rather than the drivers.",
  "This solution makes another problem worse.",
  "The sequence is unrealistic.",
  "A key problem remains unaddressed.",
  "Find a solution that changes the underlying relationship.",
];

const Building = ({ label }) => (
  <div data-testid="solutions-building" className="grid min-h-[60vh] place-items-center px-6">
    <div className="text-center">
      <CircleNotch size={26} className="mx-auto animate-spin text-foreground/70" />
      <p className="mt-4 font-serif text-xl">{label}</p>
      <p className="mt-2 text-sm text-muted-foreground">Mapping how the problems form a system, then designing solutions that ripple through and transform it.</p>
      <ThinkingTimer className="mt-5 text-center" />
    </div>
  </div>
);

export default function SolutionSpace({ sessionId, problemTitle, areasOfNeed, solutionSystems, systemsMap, causalModel, plan, planProcessing, processing, error, onChanged }) {
  const { openFeedback } = useFeedback() || {};
  const [building, setBuilding] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [reviseOpen, setReviseOpen] = useState(false);
  const [reviseText, setReviseText] = useState("");
  const [revising, setRevising] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [aiTitle, setAiTitle] = useState("");

  useEffect(() => {
    if (!sessionId) return;
    let alive = true;
    api.shortTitle(sessionId).then((d) => { if (alive && d?.title) setAiTitle(d.title); }).catch(() => {});
    return () => { alive = false; };
  }, [sessionId]);

  const build = async () => {
    setBuilding(true);
    try { await api.buildSolutionSpace(sessionId); await onChanged?.(); }
    catch (e) { toast.error("Could not build", { description: e?.response?.data?.detail || e.message }); }
    finally { setBuilding(false); }
  };
  const switchSystem = async (idx) => {
    setSwitching(true);
    try { await api.switchSystemsMap(sessionId, idx); await onChanged?.(); }
    catch (e) { toast.error("Could not switch", { description: e?.response?.data?.detail || e.message }); }
    finally { setSwitching(false); }
  };
  const buildPlan = async () => {
    try { await api.buildPlan(sessionId); setPlanOpen(true); await onChanged?.(); }
    catch (e) { toast.error("Could not build the plan", { description: e?.response?.data?.detail || e.message }); }
  };
  const submitRevise = async () => {
    const t = reviseText.trim();
    if (!t) return;
    setRevising(true);
    try { await api.reviseSolutions(sessionId, t); setReviseText(""); setReviseOpen(false); toast.success("Re-architecting the system…"); await onChanged?.(); }
    catch (e) { toast.error("Could not revise", { description: e?.response?.data?.detail || e.message }); }
    finally { setRevising(false); }
  };

  if (!areasOfNeed?.areas?.length) {
    return (
      <div data-testid="solutions-need-areas" className="mx-auto max-w-2xl px-6 py-20 text-center">
        <Lightbulb size={30} className="mx-auto mb-4 text-foreground/70" />
        <h2 className="font-serif text-3xl leading-tight">Map the problem space first</h2>
        <p className="mx-auto mt-4 max-w-prose text-[15px] leading-relaxed text-muted-foreground">The systems model is built from the problem space. Map it under the Problem Space tab, then return here.</p>
      </div>
    );
  }
  if (processing || building || switching) return <Building label={switching ? "Re-laying the map…" : "Building the systems model…"} />;

  const systems = solutionSystems?.solution_systems || [];
  if (!systemsMap?.nodes?.length || !systems.length) {
    return (
      <div data-testid="build-solutions-prompt" className="grid min-h-[55vh] place-items-center px-6 text-center">
        <div className="max-w-xl">
          <Sparkle size={30} className="mx-auto mb-4 text-foreground/70" />
          <h2 className="font-serif text-3xl">Build the systems model</h2>
          <p className="mx-auto mt-3 max-w-prose text-[15px] leading-relaxed text-muted-foreground">The engine maps how your problems form a system — the loops that keep them in place — then lays clever coordinated solutions onto the same canvas, showing how each ripples through the structure and transforms it. You can then edit any relationship yourself.</p>
          {error && <p className="mt-3 text-sm" style={{ color: "hsl(var(--destructive))" }}>Previous attempt failed: {String(error)}</p>}
          <button data-testid="build-solutions-btn" onClick={build} className="mt-7 inline-flex items-center gap-2 rounded-sm border border-primary bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/85">
            <Sparkle size={16} weight="bold" /> Build the Systems Model
          </button>
        </div>
      </div>
    );
  }

  const activeIdx = systemsMap.system_index ?? 0;
  const activeSystem = systems[activeIdx];
  const components = activeSystem?.components || [];

  // ---- the causal story (works for legacy + simple maps) ----
  const story = (systemsMap.story && systemsMap.story.current_dynamic) ? systemsMap.story : {
    current_dynamic: causalModel?.reinforcing_loops?.[0]?.explanation || causalModel?.overview || systemsMap.overview || "",
    intervention: activeSystem?.theory_of_change || activeSystem?.systemic_aim || "",
    expected_change: activeSystem?.beneficial_loops_created?.[0] || activeSystem?.why_this_is_systemic || "",
  };
  const STORY_BEATS = [
    { k: "current_dynamic", label: "The Problem", color: "#D55E00", bg: "rgba(213,94,0,0.07)" },
    { k: "intervention", label: activeSystem?.solution_system_name ? `The interventions · ${activeSystem.solution_system_name}` : "The interventions", color: "#0072B2", bg: "rgba(0,114,178,0.07)" },
    { k: "expected_change", label: "Expected system change", color: "#009E73", bg: "rgba(0,158,115,0.08)" },
  ];

  const theMap = (
    <SystemsMap sessionId={sessionId} systemsMap={systemsMap} components={components} fill={fullscreen} onChanged={onChanged} />
  );

  // ---- ordered intervention strands (primaries first) for the Strategy beat ----
  const interventionStrands = (() => {
    const nodes = systemsMap?.nodes || [];
    const edges = systemsMap?.edges || [];
    const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));
    const isInterv = (n) => n && ["intervention", "solution"].includes(n.kind);
    const interventions = nodes.filter(isInterv);
    if (!interventions.length) return [];
    const enabledInto = new Set(edges.filter((e) => isInterv(byId[e.source]) && isInterv(byId[e.target])).map((e) => e.target));
    const primaries = interventions.filter((i) => !enabledInto.has(i.id));
    const secondaries = interventions.filter((i) => enabledInto.has(i.id));
    return [...primaries, ...secondaries].map((n) => ({ label: n.label, summary: n.summary || "", detail: n.detail || "", primary: !enabledInto.has(n.id) }));
  })();

  const renderBeat = (b, opts = {}) => (
    <div className="border border-border border-l-[3px] px-4 py-3" style={{ borderLeftColor: b.color, background: opts.plain ? undefined : b.bg }}>
      <div className="font-mono text-[9.5px] font-semibold uppercase tracking-widest" style={{ color: b.color }}>{b.label}</div>
      {b.k === "intervention" && interventionStrands.length ? (
        <div className="mt-2 space-y-3">
          <ol className="space-y-2.5">
            {interventionStrands.map((s, j) => (
              <li key={j} className="leading-relaxed">
                <span className="font-serif text-[16px] font-semibold text-foreground">{j + 1}. {s.label}</span>
                <span className="ml-2 rounded-sm bg-muted px-1.5 py-0.5 align-middle font-mono text-[9px] uppercase tracking-wide text-muted-foreground">{s.primary ? "act first" : "then"}</span>
                {s.summary && <span className="mt-0.5 block font-serif text-[15px] leading-relaxed text-foreground/80">{s.summary}</span>}
                {s.detail && s.detail !== s.summary && <span className="mt-1 block text-[15px] leading-relaxed text-muted-foreground">{s.detail}</span>}
              </li>
            ))}
          </ol>
          {story.intervention && (
            <div className="border-t border-border pt-2.5">
              <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">How these work together as a system</div>
              <p className="mt-1 font-serif text-[15px] leading-relaxed text-foreground/90">{story.intervention}</p>
            </div>
          )}
        </div>
      ) : (
        <p className="mt-1.5 font-serif text-[15.5px] leading-relaxed text-foreground/90">{story[b.k] || "—"}</p>
      )}
    </div>
  );

  const shortTitle = (() => {
    const t = (problemTitle || "").trim();
    if (!t) return "Your Systemic Solution";
    const first = t.split(/[;.\n]/)[0].trim();
    return first.length > 96 ? first.slice(0, 96).trim() + "…" : first;
  })();

  return (
    <div data-testid="solution-space" className="w-full px-6 pb-28 pt-6">
      {/* Title — the problem being solved */}
      <div className="relative mb-4 w-full text-center">
        <h2 data-testid="solutions-title" className="mx-auto max-w-4xl font-serif text-3xl font-bold leading-tight">{aiTitle || shortTitle}</h2>
        <button data-testid="rebuild-solutions-btn" onClick={build} className="absolute right-0 top-0 inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><ArrowClockwise size={12} /> Rebuild</button>
      </div>

      {/* The Problem — above the diagram */}
      {story.current_dynamic && (
        <div data-testid="problem-beat" className="mb-3 w-full">
          {renderBeat(STORY_BEATS[0], { plain: true })}
        </div>
      )}

      {/* THE DIAGRAM — front & center, Enlarge lives inside the box */}
      <div className="w-full">
        {!fullscreen && (
          <div className="relative overflow-hidden rounded-lg border border-slate-300 bg-slate-100 shadow-lg ring-1 ring-black/5 dark:border-slate-700 dark:bg-slate-900/60">
            {theMap}
            <button data-testid="expand-map-btn" onClick={() => setFullscreen(true)} className="absolute right-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-md border border-border bg-card/95 px-3 py-1.5 text-xs font-medium shadow-md backdrop-blur transition-colors hover:bg-muted"><ArrowsOut size={13} /> Enlarge</button>
          </div>
        )}
        <p data-testid="map-directions" className="mt-3 w-full text-center text-[13.5px] font-semibold italic leading-relaxed text-foreground/80">
          Explore your systemic solution. The inner boxes are your problems; the outer boxes are your solutions. The lines show how the problems and solutions are related to each other. Click on any box or line for an explanation of how to put your solutions into action. If this doesn't seem right, let's talk — describe what you think is wrong below, and let's rebuild.
        </p>
      </div>

      {/* Strategies — below the figure */}
      {(story.intervention || story.expected_change) && (
        <div data-testid="causal-story" className="mt-6 flex w-full flex-col gap-3">
          {STORY_BEATS.slice(1).map((b) => <div key={b.k}>{renderBeat(b)}</div>)}
        </div>
      )}

      {fullscreen && (
        <div data-testid="fullscreen-workspace" className="fixed inset-0 z-[60] flex flex-col bg-background">
          <div className="flex items-center justify-between border-b border-border px-5 py-2.5">
            <div>
              <span className="font-serif text-lg">The Systems Model</span>
              <span className="ml-3 text-xs text-muted-foreground">Pan · zoom · click a relationship to explore it</span>
            </div>
            <button data-testid="close-fullscreen-btn" onClick={() => setFullscreen(false)} className="inline-flex items-center gap-1.5 rounded-none border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted"><ArrowsIn size={13} /> Close</button>
          </div>
          <div className="min-h-0 flex-1">{theMap}</div>
        </div>
      )}

      {/* revise + controls */}
      <div data-testid="revise-box" className="mt-3 w-full rounded-sm border border-dashed border-border bg-card p-4 text-center">
        {!reviseOpen ? (
          <button data-testid="open-revise-btn" onClick={() => setReviseOpen(true)} className="mx-auto inline-flex items-center gap-2 text-sm font-medium text-foreground/80 hover:text-foreground"><PaperPlaneRight size={14} /> Not right? Tell the engine what's wrong and it will re-architect the whole system.</button>
        ) : (
          <div className="text-left">
            <div className="mb-2 flex items-center justify-between"><span className="text-sm font-medium">What should change?</span><button onClick={() => setReviseOpen(false)} className="text-muted-foreground hover:text-foreground"><X size={15} /></button></div>
            <div className="mb-2 flex flex-wrap gap-1.5">{REVISE_SUGGESTIONS.map((s, i) => <button key={i} onClick={() => setReviseText(s)} className="rounded-full border border-border px-2.5 py-1 text-[11px] hover:bg-muted">{s}</button>)}</div>
            <div className="relative">
              <textarea data-testid="revise-input" value={reviseText} onChange={(e) => setReviseText(e.target.value)} rows={3} placeholder="e.g. The team-activation solution should come before the market test; and it makes the cash problem worse — mitigate that." className="w-full resize-none rounded-sm border border-border bg-background p-2.5 pr-11 text-sm outline-none focus:border-foreground" />
              <MicButton size={17} className="absolute right-2.5 top-2.5" onResult={(t) => setReviseText((p) => (p ? p.trim() + " " + t : t))} />
            </div>
            <button data-testid="submit-revise-btn" onClick={submitRevise} disabled={revising || !reviseText.trim()} className="mt-2 inline-flex items-center gap-2 rounded-sm border border-primary bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/85 disabled:opacity-40">
              {revising ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneRight size={14} weight="bold" />} Re-architect the system
            </button>
          </div>
        )}
      </div>

      <div className="mt-3 flex w-full flex-wrap items-center justify-center gap-2">
        <button data-testid="build-plan-btn" onClick={buildPlan} disabled={planProcessing} className="inline-flex items-center gap-1.5 rounded-sm border border-primary bg-primary px-4 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/85 disabled:opacity-50">
          {planProcessing ? <CircleNotch size={13} className="animate-spin" /> : <ListChecks size={14} weight="bold" />}{planProcessing ? "Building the plan…" : plan ? "Rebuild the plan" : "Build the plan"}
        </button>
        {plan && !planProcessing && <button data-testid="view-plan-btn" onClick={() => setPlanOpen(true)} className="inline-flex items-center gap-1.5 rounded-sm border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted"><ListChecks size={13} /> View the plan</button>}
        <button data-testid="print-report-btn" onClick={() => window.open(`/sessions/${sessionId}/report`, "_blank")} className="inline-flex items-center gap-1.5 rounded-sm border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted"><Printer size={13} /> Print / Export</button>
        <a data-testid="return-cgi-btn" href="https://www.commongroundinstitute.net" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-sm border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted"><ArrowUUpLeft size={13} /> Return to Common Ground Institute</a>
      </div>

      <div data-testid="feedback-card" className="mt-8 w-full rounded-lg border border-primary/30 bg-primary/[0.05] px-6 py-5 text-center">
        <Megaphone size={20} weight="fill" className="mx-auto text-primary" />
        <h3 className="mt-2 font-serif text-xl">We'd love your feedback</h3>
        <p className="mx-auto mt-1.5 max-w-[620px] text-sm text-muted-foreground">This tool is still in active development, and your input directly shapes where it goes next. What was useful? What was confusing? What would you love to see?</p>
        <button data-testid="feedback-card-btn" onClick={() => openFeedback?.()} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"><PaperPlaneRight size={15} weight="bold" /> Share your feedback</button>
      </div>

      <Dialog open={planOpen} onOpenChange={setPlanOpen}>
        <DialogContent data-testid="plan-dialog" className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Your action plan</DialogTitle></DialogHeader>
          {planProcessing && !plan ? (
            <div className="grid place-items-center py-12 text-center"><div><CircleNotch size={24} className="mx-auto animate-spin text-foreground/70" /><p className="mt-3 text-sm text-muted-foreground">Sequencing the system into a plan…</p></div></div>
          ) : plan ? (
            <div className="space-y-5">
              {plan.headline && <p className="font-serif text-[17px] leading-relaxed text-foreground/90">{plan.headline}</p>}
              <ol className="space-y-4">
                {(plan.phases || []).map((ph, i) => (
                  <li key={i} data-testid={`plan-phase-${i}`} className="border-l-2 border-foreground/30 pl-4">
                    <div className="flex items-baseline gap-2"><span className="font-mono text-xs text-muted-foreground">{String(i + 1).padStart(2, "0")}</span><h4 className="font-serif text-lg leading-tight">{ph.title}</h4>{ph.timeframe && <span className="rounded-sm bg-muted px-1.5 py-0.5 font-mono text-[10px] uppercase text-muted-foreground">{ph.timeframe}</span>}</div>
                    <ul className="mt-2 space-y-1">{(ph.steps || []).map((st, j) => <li key={j} className="flex gap-2 text-sm leading-snug"><ArrowRight size={13} className="mt-1 shrink-0 text-foreground/60" /> {st}</li>)}</ul>
                  </li>
                ))}
              </ol>
            </div>
          ) : <p className="py-8 text-center text-sm text-muted-foreground">No plan yet.</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
